import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type MenuItemSpecsFragment } from '../../../../api/fragments/MenuItemSpecs';
import { type RouterSpecsFragment } from '../../../../api/fragments/RouterSpecs';
import {
    UpdateRouterMenuDocument,
    type UpdateRouterMenuMutation,
    type UpdateRouterMenuMutationVariables,
} from '../../../../api/mutations/updateRouterMenu';
import { type MenuItemSettings } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import useHandleError from '../../../../utilities/useHandleError';
import RouterMenuItemTable from './RouterMenuItemTable';

export enum RouterMenuType {
    Endpoint = 'endpoint',
    Custom = 'custom',
    LogOut = 'logout',
}

export type RouterMenuItemProps = {
    router: RouterSpecsFragment;
};

type RouterMenuFormValue = MenuItemSettings[];

export const getMenuItemValue = (menuItems: MenuItemSpecsFragment[]) =>
    menuItems.map(item => {
        switch (item.__typename) {
            case 'MenuCustomPathItem': {
                return {
                    customPathItem: {
                        ...omit(['id', '__typename'], item),
                    },
                };
            }

            case 'MenuEndpointItem': {
                return {
                    endpointItem: {
                        ...omit(['id', 'endpoint', '__typename'], item),
                    },
                };
            }

            default: {
                return {
                    logoutActionItem: {
                        ...omit(['id', '__typename'], item),
                    },
                };
            }
        }
    });

const RouterMenuItem = ({ router }: RouterMenuItemProps) => {
    const { t } = useTranslation('routerDetails');

    const apolloClient = useApolloClient();

    const [dataSource, setDataSource] = useState<MenuItemSpecsFragment[]>(router.menuItems);
    const initialValues: RouterMenuFormValue = useMemo(() => getMenuItemValue(dataSource), [dataSource]);

    useEffect(() => {
        setDataSource(router.menuItems);
    }, [router.menuItems]);

    const onSubmit = useHandleError<RouterMenuFormValue>(
        async values => {
            message.loading({
                content: t('routerDetails:menuItems.message.updatingMenuItems'),
                key: 'primary',
                duration: 0,
            });
            await apolloClient.mutate<UpdateRouterMenuMutation, UpdateRouterMenuMutationVariables>({
                mutation: UpdateRouterMenuDocument,
                variables: {
                    routerId: router.id,
                    menuItems: values,
                },
            });
            message.success({
                content: t('routerDetails:menuItems.message.successUpdatedMenuItems'),
                key: 'primary',
            });
        },
        [apolloClient, router.id, t]
    );

    return (
        <CollapsibleWrapper defaultActiveKey={t('routerDetails:menuItems.title')}>
            <Panel key={t('routerDetails:menuItems.title')} header={t('routerDetails:menuItems.title')}>
                <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
                    {({ handleSubmit }) => (
                        <Form id="routerMenuItemForm" onSubmitCapture={handleSubmit}>
                            <RouterMenuItemTable dataSource={dataSource} setDataSource={setDataSource} />
                        </Form>
                    )}
                </Formik>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default RouterMenuItem;
